import { useMemo, type ReactNode } from 'react';
import { AreaChart, ResponsiveContainer, YAxis } from 'recharts';
import { useI18nContext } from '@/i18n/i18n-react';
import type {
  RevenueChartResponseDto,
  TenantSalesMetricsResponseDto,
} from '@/generated/korza-api-client/types.gen';
import { appShare, appShareRows } from '@/lib/app-share';
import type { ChartGranularity } from '@/lib/chart-axis';
import { usePartialBuckets } from '@/lib/hooks/use-partial-buckets';
import { markPartialRows, type ChartRange } from '@/lib/partial-buckets';
import type { DeltaView } from '@/lib/period-comparison';
import { formatCurrency } from '@/components/metrics/formatters';
import { DeltaBadge } from '@/components/metrics/delta-badge';
import { MetricsChartAreaSeries } from '@/components/ui/charts/chart-area-series';
import { HelpIconButton } from '@/components/help-panel/help-icon-button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

export interface AppShareBandProps {
  salesMetrics: TenantSalesMetricsResponseDto | undefined;
  /** In percentage points against the previous period (CMP-04). */
  delta: DeltaView;
  /** "vs. …" next to the variation. */
  comparison?: ReactNode;
  revenueChart: RevenueChartResponseDto | undefined;
  range?: ChartRange;
  granularity: ChartGranularity;
  currency: string;
}

/**
 * "Participação do app" (BACK-04): a slim full-width band with the app's
 * share of the store's revenue in the period (1 decimal), its variation in
 * p.p., the app and store totals and a small line per bucket (partial bucket
 * dashed; buckets without sales off the line). "—" without base.
 */
export function AppShareBand({
  salesMetrics,
  delta,
  comparison,
  revenueChart,
  range,
  granularity,
  currency,
}: AppShareBandProps) {
  const { LL, locale } = useI18nContext();
  const share = salesMetrics
    ? appShare(salesMetrics.totalRevenue, salesMetrics.web.revenue)
    : null;

  const rows = useMemo(
    () => appShareRows(revenueChart?.points ?? [], granularity, locale),
    [revenueChart, granularity, locale]
  );
  const partial = usePartialBuckets(rows, range, granularity);
  const data = useMemo(
    () => markPartialRows(rows, 'share', partial.edges),
    [rows, partial.edges]
  );

  return (
    <Card
      data-app-share-band
      className="grid items-center gap-x-7 gap-y-4 px-5 py-4 md:grid-cols-[300px_minmax(0,1fr)]"
    >
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold">
            {LL.metricsPage.salesTab.appShare.title()}
          </h3>
          <HelpIconButton helpKey="appShare" />
        </div>
        {salesMetrics ? (
          <>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <span
                data-app-share-value
                className="text-[22px] font-semibold tabular-nums tracking-[-0.02em]"
              >
                {share === null
                  ? '—'
                  : new Intl.NumberFormat(locale, {
                      style: 'percent',
                      minimumFractionDigits: 1,
                      maximumFractionDigits: 1,
                    }).format(share)}
              </span>
              {share === null ? null : (
                <DeltaBadge delta={delta} comparison={comparison} />
              )}
            </div>
            {share === null ? null : (
              <p className="text-xs leading-snug text-muted-foreground">
                {LL.metricsPage.salesTab.appShare.text({
                  app: formatCurrency(
                    salesMetrics.totalRevenue,
                    locale,
                    currency
                  ),
                  store: formatCurrency(
                    salesMetrics.totalRevenue + salesMetrics.web.revenue,
                    locale,
                    currency
                  ),
                })}
              </p>
            )}
          </>
        ) : (
          <Skeleton className="h-8 w-40" />
        )}
      </div>
      <div
        role="img"
        aria-label={LL.metricsPage.salesTab.appShare.chartLabel()}
        data-app-share-chart
        className="h-[84px] min-w-0"
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 6, right: 6, bottom: 6, left: 6 }}
          >
            <YAxis hide domain={[0, 'auto']} />
            <MetricsChartAreaSeries
              dataKey="share"
              partialDataKey="sharePartial"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
